/**
 * Indexable position path pages (Step 1b SEO).
 *
 * Prefer path URLs over ?pos= query strings so prerendered HTML can carry the
 * correct robots/canonical. Shared by indexabilityPolicy, sitemap, and the app.
 *
 * Paths:
 *   /rankings/{stat}/{pos}           — yearless hub (current season data)
 *   /rankings/{stat}/{year}/{pos}    — fixed season
 */

/** @typedef {{ statCategory: string, position: string, year?: number }} PositionPathEntry */

/** @type {PositionPathEntry[]} */
const POSITION_PATH_ALLOWLIST = [
  { statCategory: 'receiving', position: 'wr' },
  { statCategory: 'rushing', position: 'rb' },
  { statCategory: 'passing', position: 'qb' },
  { statCategory: 'receiving', position: 'wr', year: 2025 },
  { statCategory: 'rushing', position: 'rb', year: 2025 },
  { statCategory: 'receiving', position: 'te' },
  { statCategory: 'defense', position: 'db' },
];

/** Tokens that may appear as a path position segment (groups only, not presets). */
const POSITION_PATH_TOKENS = new Set([
  'qb', 'rb', 'wr', 'te', 'ol', 'dl', 'lb', 'db', 'k', 'p', 'ls',
]);

/**
 * @param {string|null|undefined} token
 * @returns {boolean}
 */
function isPositionPathToken(token) {
  return Boolean(token) && POSITION_PATH_TOKENS.has(String(token).toLowerCase());
}

/**
 * @param {{ statCategory?: string, position?: string|null, year?: number, urlOmitsYear?: boolean }} ctx
 * @returns {boolean}
 */
function matchesPositionPathAllowlist(ctx) {
  const stat = ctx.statCategory || '';
  const pos = ctx.position ? String(ctx.position).toLowerCase() : '';
  if (!pos) return false;
  const yearless = !!ctx.urlOmitsYear || ctx.year == null;
  return POSITION_PATH_ALLOWLIST.some((entry) => {
    if ((entry.statCategory || '') !== stat) return false;
    if (entry.position !== pos) return false;
    if (entry.year == null) return yearless;
    return !yearless && entry.year === ctx.year;
  });
}

/**
 * Build the path for an allowlist entry (or any stat/pos/year combo using the same shape).
 * @param {string} statCategory
 * @param {string} position
 * @param {number} [year]
 * @returns {string}
 */
function getPositionPathUrl(statCategory, position, year) {
  const pos = String(position).toLowerCase();
  let url = '/rankings';
  if (statCategory) url += `/${statCategory}`;
  if (year != null) url += `/${year}`;
  url += `/${pos}`;
  return url;
}

module.exports = {
  POSITION_PATH_ALLOWLIST,
  POSITION_PATH_TOKENS,
  isPositionPathToken,
  matchesPositionPathAllowlist,
  getPositionPathUrl,
};
